import React, { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { Button, CircularProgress } from "@mui/material";
import Icon from "@mdi/react";
import {
	mdiCheckBold,
	mdiCloseThick,
	mdiRefresh,
	mdiChevronDown,
	mdiChevronRight,
	mdiClockOutline,
} from "@mdi/js";
import { MemberAvatar } from "components";
import { useElapsedTime } from "shared/hooks";
import {
	NotistackServices,
	SubtaskReviewAction,
	TeamLeaderServices,
	TeamTaskSubtaskDTO,
} from "shared/services";
import { HandleError } from "shared/utils";
import { DeliverableList } from "./DeliverableList";
import { SubtaskToolCallState } from "../types";
import styles from "./SubtaskReviewPanel.module.scss";

interface SubtaskReviewPanelProps {
	workspaceId: string;
	taskId: string;
	subtasks: TeamTaskSubtaskDTO[];
	memberNamesById: Record<string, string>;
	toolCallBySubtaskId?: Record<string, SubtaskToolCallState>;
	onReviewed?: (subtask: TeamTaskSubtaskDTO) => void;
}

const STATUS_LABEL: Record<string, string> = {
	pending: "Pendente",
	processing: "Em execução",
	completed: "Concluída",
	failed: "Falha",
	paused: "Pausada",
};

const REVIEW_LABEL: Record<string, string> = {
	approved: "Aprovada",
	rejected: "Rejeitada",
	needs_changes: "Precisa de ajustes",
};

const isReviewable = (status: string, reviewStatus: string | null) =>
	(status === "completed" || status === "failed") && reviewStatus !== "approved";

/**
 * Linha individual de uma subtarefa — separada para poder usar hooks (useElapsedTime)
 * por subtarefa sem violar regras de hooks no .map() do parent.
 */
const SubtaskRow: React.FC<{
	subtask: TeamTaskSubtaskDTO;
	memberName: string;
	workspaceId: string;
	taskId: string;
	loading: boolean;
	reopenInput: string;
	isReopening: boolean;
	toolCall?: SubtaskToolCallState;
	onChangeReopen: (value: string) => void;
	onStartReopen: () => void;
	onCancelReopen: () => void;
	onSubmit: (action: SubtaskReviewAction, notes?: string) => void;
}> = ({ subtask, memberName, workspaceId, taskId, loading, reopenInput, isReopening, toolCall, onChangeReopen, onStartReopen, onCancelReopen, onSubmit }) => {
	const elapsed = useElapsedTime(subtask.startedAt, subtask.completedAt, 1000);
	const reviewable = isReviewable(subtask.status, subtask.reviewStatus);
	const lastOutput = (subtask.executionContext as any)?.last_result?.content as string | undefined;

	return (
		<div className={clsx(styles.subtask, styles[`subtask_${subtask.status}` as const])}>
			<div className={styles.subtaskHead}>
				<MemberAvatar id={subtask.teamMemberId} name={memberName} size="sm" status={subtask.status} />

				<div className={styles.subtaskInfo}>
					<div className={styles.subtaskTitleRow}>
						<span className={styles.subtaskTitle}>{subtask.title}</span>
						<span className={clsx(styles.statusLabel, styles[`status_${subtask.status}` as const])}>
							{STATUS_LABEL[subtask.status] ?? subtask.status}
						</span>
						{subtask.reviewStatus && (
							<span className={clsx(styles.reviewChip, styles[`review_${subtask.reviewStatus}` as const])}>
								{REVIEW_LABEL[subtask.reviewStatus]}
							</span>
						)}
					</div>
					<div className={styles.subtaskMeta}>
						<span>{memberName}</span>
						{subtask.startedAt && (
							<span className={styles.elapsedRow}>
								<Icon path={mdiClockOutline} size={0.55} />
								{elapsed}
							</span>
						)}
						{subtask.interactionCount > 0 && (
							<span className={styles.interactionBadge}>tentativa {subtask.interactionCount}</span>
						)}
					</div>
					{/* Etapa descritiva (ação atual) — destacada quando processing. */}
					{subtask.status === "processing" && subtask.statusDescription && (
						<div className={styles.actionLine}>
							<span className={styles.actionDot} />
							{subtask.statusDescription}
						</div>
					)}
					{/* Feedback em tempo real da tool MCP em execução (chamada/concluída).
					    Mostra o usuário que algo está rolando enquanto o LLM processa. */}
					{toolCall && subtask.status === "processing" && (
						<div className={styles.actionLine}>
							{toolCall.phase === "start" ? (
								<>
									<CircularProgress size={10} className={styles.toolSpinner} />
									Chamando {toolCall.mcpName ?? toolCall.mcpSlug ?? "ferramenta"} · <code>{toolCall.toolName}</code>
								</>
							) : (
								<>
									<span className={styles.actionDot} />
									{toolCall.success
										? `${toolCall.mcpName ?? toolCall.mcpSlug ?? "Ferramenta"} respondeu em ${toolCall.durationMs ?? "?"}ms`
										: `Falha em ${toolCall.toolName}${toolCall.error ? `: ${toolCall.error}` : ""}`}
								</>
							)}
						</div>
					)}
				</div>
			</div>

			{lastOutput && (
				<div className={styles.output}>
					<div className={styles.outputLabel}>Resposta do membro:</div>
					<div className={styles.outputBody}>{lastOutput}</div>
				</div>
			)}

			{/* Histórico de entregáveis (e-mails enviados, eventos criados, etc.) */}
			{(subtask.status === "completed" || subtask.status === "failed") && (
				<DeliverableList
					workspaceId={workspaceId}
					taskId={taskId}
					subtaskId={subtask.id}
					refreshKey={subtask.updatedAt}
				/>
			)}

			{subtask.reviewNotes && (
				<div className={styles.reviewNotes}>
					<strong>Nota da revisão anterior:</strong> {subtask.reviewNotes}
				</div>
			)}

			{reviewable && (
				<div className={styles.actions}>
					{!isReopening ? (
						<>
							<Button
								size="small"
								variant="contained"
								color="success"
								startIcon={<Icon path={mdiCheckBold} size={0.7} />}
								disabled={loading}
								onClick={() => onSubmit("approve")}
							>
								Aprovar
							</Button>
							<Button
								size="small"
								variant="outlined"
								color="error"
								startIcon={<Icon path={mdiCloseThick} size={0.7} />}
								disabled={loading}
								onClick={() => onSubmit("reject")}
							>
								Rejeitar
							</Button>
							<Button
								size="small"
								variant="outlined"
								startIcon={<Icon path={mdiRefresh} size={0.7} />}
								disabled={loading}
								onClick={onStartReopen}
							>
								Reabrir com instruções
							</Button>
							{loading && <CircularProgress size={16} />}
						</>
					) : (
						<div className={styles.reopenBox}>
							<textarea
								className={styles.reopenTextarea}
								placeholder="Descreva o que precisa ser ajustado (ex.: 'usar tom mais formal', 'incluir CC para X', etc.)"
								value={reopenInput}
								onChange={(e) => onChangeReopen(e.target.value)}
								rows={3}
							/>
							<div className={styles.reopenActions}>
								<Button size="small" onClick={onCancelReopen} disabled={loading}>
									Cancelar
								</Button>
								<Button
									size="small"
									variant="contained"
									disabled={loading || reopenInput.trim().length < 3}
									onClick={() => onSubmit("reopen", reopenInput)}
								>
									Reabrir e re-executar
								</Button>
								{loading && <CircularProgress size={16} />}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export const SubtaskReviewPanel: React.FC<SubtaskReviewPanelProps> = ({
	workspaceId,
	taskId,
	subtasks,
	memberNamesById,
	toolCallBySubtaskId,
	onReviewed,
}) => {
	const [expanded, setExpanded] = useState(false);
	const [activeAction, setActiveAction] = useState<{ subtaskId: string; action: SubtaskReviewAction } | null>(null);
	const [reopenInputs, setReopenInputs] = useState<Record<string, string>>({});
	const [loadingId, setLoadingId] = useState<string | null>(null);

	const reviewableCount = useMemo(
		() => subtasks.filter((s) => isReviewable(s.status, s.reviewStatus)).length,
		[subtasks],
	);

	const submit = useCallback(
		async (subtask: TeamTaskSubtaskDTO, action: SubtaskReviewAction, notes?: string) => {
			setLoadingId(subtask.id);
			try {
				const updated = await TeamLeaderServices.reviewSubtask(workspaceId, taskId, subtask.id, {
					action,
					notes: notes ?? null,
				});
				NotistackServices.showSuccessMessage(
					action === "approve"
						? "Subtarefa aprovada"
						: action === "reject"
							? "Subtarefa rejeitada"
							: "Subtarefa reaberta — membro vai re-executar",
				);
				onReviewed?.(updated);
				if (action === "reopen") {
					setReopenInputs((prev) => ({ ...prev, [subtask.id]: "" }));
					setActiveAction(null);
				}
			} catch (err) {
				NotistackServices.showErrorMessage(
					HandleError.isHandleError(err) ? err.message : "Falha ao registrar revisão",
				);
			} finally {
				setLoadingId(null);
			}
		},
		[workspaceId, taskId, onReviewed],
	);

	if (subtasks.length === 0) return null;

	return (
		<div className={styles.panel}>
			<button
				type="button"
				className={styles.header}
				onClick={() => setExpanded((v) => !v)}
			>
				<Icon path={expanded ? mdiChevronDown : mdiChevronRight} size={0.7} />
				<span>
					Subtarefas <strong>({subtasks.length})</strong>
					{reviewableCount > 0 && (
						<span className={styles.badge}>{reviewableCount} para revisar</span>
					)}
				</span>
			</button>

			{expanded && (
				<div className={styles.body}>
					{subtasks.map((subtask) => {
						const memberName = memberNamesById[subtask.teamMemberId] ?? "Membro";
						const isReopening = activeAction?.subtaskId === subtask.id && activeAction.action === "reopen";

						return (
							<SubtaskRow
								key={subtask.id}
								subtask={subtask}
								memberName={memberName}
								workspaceId={workspaceId}
								taskId={taskId}
								loading={loadingId === subtask.id}
								reopenInput={reopenInputs[subtask.id] ?? ""}
								isReopening={isReopening}
								toolCall={toolCallBySubtaskId?.[subtask.id]}
								onChangeReopen={(value) =>
									setReopenInputs((prev) => ({ ...prev, [subtask.id]: value }))
								}
								onStartReopen={() =>
									setActiveAction({ subtaskId: subtask.id, action: "reopen" })
								}
								onCancelReopen={() => setActiveAction(null)}
								onSubmit={(action, notes) => submit(subtask, action, notes)}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
};
